import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Thermometer,
  Droplet,
  Wind,
  Sun,
  Clock,
  RefreshCw,
  Grid3x3,
  AlertTriangle,
  ArrowRight,
  FileText,
  FileCheck,
  FileX,
  Loader,
  Zap,
  Users,
  Shield,
  Eye,
  User
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, Button } from '../components/ui';
import { useLayout } from '../contexts/LayoutContext';
import { getDashboardMetrics, getSystemStatus, getTopStations } from '../utils/dashboardData';
import { mapFarms } from '../utils/mapMockData';
import { MOCK_USERS, MOCK_STATS } from '../utils/mockUsersData';
import { ViewUserModal } from '../components/users/ViewUserModal';
import { useLanguageNavigation } from '../components/routing/LanguageWrapper';
import './DashboardPage.css';
import '../pages/ReportsPage.css';

function formatTimeAgo(date) {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  // NOTE: reuse stations.time.* since it's already localized and generic
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { selectedFarm: contextFarm } = useLayout();
  const currentFarm = mapFarms.find(f => f.id === contextFarm?.id || f.name === contextFarm?.name) || mapFarms[0];
  
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [selectedMetric, setSelectedMetric] = useState('temperature');

  const dashboardMetrics = useMemo(() => getDashboardMetrics(currentFarm, lastRefresh), [currentFarm, lastRefresh]);
  const metricById = useMemo(
    () => Object.fromEntries(dashboardMetrics.map((metric) => [metric.id, metric])),
    [dashboardMetrics]
  );
  const campoMetrics = useMemo(() => ({
    avgTemp: metricById.temperature?.byType?.campo_aperto ?? 0,
    avgHumidity: metricById.humidity?.byType?.campo_aperto ?? 0,
    avgWind: metricById.wind?.byType?.campo_aperto ?? 0,
    avgSolar: metricById.solar?.byType?.campo_aperto ?? 0,
  }), [metricById]);
  const retroMetrics = useMemo(() => ({
    avgTemp: metricById.temperature?.byType?.retro_modulo ?? 0,
    avgHumidity: metricById.humidity?.byType?.retro_modulo ?? 0,
    avgWind: metricById.wind?.byType?.retro_modulo ?? 0,
    avgSolar: metricById.solar?.byType?.retro_modulo ?? 0,
  }), [metricById]);
  
  const systemStatus = getSystemStatus(currentFarm);
  
  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setLastRefresh(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!dashboardMetrics.some((metric) => metric.id === selectedMetric)) {
      setSelectedMetric(dashboardMetrics[0]?.id || 'temperature');
    }
  }, [dashboardMetrics, selectedMetric]);
  
  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      setRefreshing(false);
      setLastRefresh(new Date());
    }, 1000);
  };

  const getMetricLabel = (metricId) => {
    switch (metricId) {
      case 'temperature':
        return t('analytics.metrics.temperature');
      case 'humidity':
        return t('analytics.metrics.humidity');
      case 'wind':
        return t('analytics.metrics.windSpeed');
      case 'solar':
        return t('analytics.metrics.radiation');
      default:
        return metricId;
    }
  };
  
  return (
    <div className="dashboard">
      {/* System Status Row */}
      <div className="dashboard__section">
        <div className="dashboard__section-header">
          <h2 className="dashboard__section-title">{t('dashboard.sections.systemStatus.title')}</h2>
          <p className="dashboard__section-subtitle">{t('dashboard.sections.systemStatus.subtitle')}</p>
        </div>
        <div className="dashboard__status-row">
          <CombinedStatusCard systemStatus={systemStatus} />
          <AlertsCard />
        </div>
        <div className="dashboard__type-cards">
          <TypeBreakdownCard
            type="campo_aperto"
            icon={<Sun size={32} />}
            title={t('dashboard.stationTypes.openField.title')}
            subtitle={t('dashboard.stationTypes.openField.subtitle')}
            count={systemStatus.campoApertoCount}
            activeCount={systemStatus.campoActive}
            metrics={campoMetrics}
            metricById={metricById}
          />
          <TypeBreakdownCard
            type="retro_modulo"
            icon={<Grid3x3 size={32} />}
            title={t('dashboard.stationTypes.underPanel.title')}
            subtitle={t('dashboard.stationTypes.underPanel.subtitle')}
            count={systemStatus.retroModuloCount}
            activeCount={systemStatus.retroActive}
            metrics={retroMetrics}
            metricById={metricById}
          />
        </div>
      </div>
      
      {/* Metric Cards with Charts - Tabbed Interface */}
      <div className="dashboard__section">
        <div className="dashboard__section-header">
          <h2 className="dashboard__section-title">{t('dashboard.sections.sensorData.title')}</h2>
          <p className="dashboard__section-subtitle">{t('dashboard.sections.sensorData.subtitle')}</p>
        </div>
        <div className="dashboard__metrics-tabs">
        <div className="dashboard__metrics-tabs-sidebar">
          {dashboardMetrics.map((metric) => {
            const Icon = metric.id === 'temperature' ? Thermometer :
                         metric.id === 'humidity' ? Droplet :
                         metric.id === 'wind' ? Wind : Sun;
            const isActive = selectedMetric === metric.id;
            
            return (
              <button
                key={metric.id}
                className={`dashboard__metrics-tab ${isActive ? 'dashboard__metrics-tab--active' : ''}`}
                onClick={() => setSelectedMetric(metric.id)}
              >
                <div className="dashboard__metrics-tab-icon" style={{ 
                  backgroundColor: isActive ? `${metric.color}14` : 'transparent',
                  color: isActive ? metric.color : 'var(--color-gray-600)'
                }}>
                  <Icon size={20} />
                </div>
                <span className="dashboard__metrics-tab-label">{getMetricLabel(metric.id)}</span>
                {metric.trend && (
                  <div className={`dashboard__metrics-tab-trend dashboard__metrics-tab-trend--${metric.trend.direction}`}>
                    {metric.trend.direction === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        
        <div className="dashboard__metrics-content">
          <div className="dashboard__metrics-main">
            {dashboardMetrics
              .filter(metric => metric.id === selectedMetric)
              .map((metric) => (
                <MetricCard key={metric.id} metric={metric} />
              ))}
          </div>
          <div className="dashboard__metrics-sidebar">
            <ReportsStatusCard />
          </div>
        </div>
        </div>
      </div>
      
      {/* Access Section */}
      <div className="dashboard__section">
        <div className="dashboard__section-header">
          <h2 className="dashboard__section-title">{t('dashboard.sections.platformAccess.title')}</h2>
          <p className="dashboard__section-subtitle">{t('dashboard.sections.platformAccess.subtitle')}</p>
        </div>
        <div className="dashboard__access-row">
          <AccessCard />
        </div>
      </div>
      
    </div>
  );
}

// Combined Status Card Component
function CombinedStatusCard({ systemStatus }) {
  const { t } = useTranslation();
  const { navigateTo } = useLanguageNavigation();
  const uptimePercentage = ((systemStatus.activeStations / systemStatus.totalStations) * 100).toFixed(1);
  
  return (
    <Card className="dashboard__combined-status-card" hoverable>
      <div className="dashboard__combined-status-header">
        <div className="dashboard__status-icon" style={{ backgroundColor: 'var(--color-primary-light)' }}>
          <Activity size={24} style={{ color: 'var(--color-primary)' }} />
        </div>
        <div className="dashboard__status-label">{t('dashboard.cards.stationStatus.title')}</div>
        <Button
          variant="secondary"
          size="sm"
          icon={<ArrowRight size={16} />}
          onClick={() => navigateTo('/stations')}
          className="dashboard__view-stations-btn"
        >
          {t('common.view')}
        </Button>
      </div>
      
      <div className="dashboard__combined-status-content">
        {/* Total Stations Component */}
        <div className="dashboard__status-component dashboard__status-component--total">
          <div className="dashboard__status-component-content">
            <div className="dashboard__status-component-left">
              <div className="dashboard__status-component-label">{t('stations.stats.total')}</div>
              <div className="dashboard__status-component-value">{systemStatus.totalStations}</div>
            </div>
            <Activity size={20} style={{ color: 'var(--color-primary)' }} />
          </div>
        </div>
        
        {/* Active Stations Component */}
        <div className="dashboard__status-component dashboard__status-component--active">
          <div className="dashboard__status-component-content">
            <div className="dashboard__status-component-left">
              <div className="dashboard__status-component-label">{t('stations.stats.active')}</div>
              <div className="dashboard__status-component-value">{systemStatus.activeStations}</div>
              <div className="dashboard__status-component-subtitle">
                <CheckCircle size={12} style={{ color: 'var(--color-success)' }} />
                <span>{t('dashboard.cards.stationStatus.uptime', { percent: uptimePercentage })}</span>
              </div>
            </div>
            <CheckCircle size={20} style={{ color: 'var(--color-success)' }} />
      </div>
        </div>
        
        {/* Inactive Stations Component */}
        <div className="dashboard__status-component dashboard__status-component--inactive">
          <div className="dashboard__status-component-content">
            <div className="dashboard__status-component-left">
              <div className="dashboard__status-component-label">{t('stations.stats.inactive')}</div>
              <div className="dashboard__status-component-value">{systemStatus.inactiveStations}</div>
              <div className="dashboard__status-component-breakdown">
          <span>
            <span className="dashboard__type-indicator" style={{ backgroundColor: 'var(--color-success)' }} />
                  {t('dashboard.cards.stationStatus.breakdown.campo', { count: systemStatus.campoApertoCount })}
          </span>
          <span>
            <span className="dashboard__type-indicator" style={{ backgroundColor: 'var(--color-primary)' }} />
                  {t('dashboard.cards.stationStatus.breakdown.retro', { count: systemStatus.retroModuloCount })}
                </span>
              </div>
            </div>
            <XCircle size={20} style={{ color: 'var(--color-gray-600)' }} />
          </div>
        </div>
      </div>
    </Card>
  );
}

// Alerts Card Component
function AlertsCard() {
  const { t } = useTranslation();
  // Mock alerts data - in real app, this would come from API
  const stationAlerts = []; // Empty for "all clear" state
  const divergenceAlerts = []; // Empty for "all clear" state

  return (
    <Card className="dashboard__alerts-card" hoverable>
      <div className="dashboard__alerts-header">
        <div className="dashboard__status-icon" style={{ backgroundColor: 'rgba(214, 175, 50, 0.18)' }}>
          <AlertTriangle size={24} style={{ color: 'var(--color-warning)' }} />
        </div>
        <div className="dashboard__status-label">{t('dashboard.alerts')}</div>
      </div>
      
      <div className="dashboard__alerts-content">
        <div className="dashboard__alerts-section">
          <h3 className="dashboard__alerts-section-title">{t('dashboard.cards.alerts.stationSpecificTitle')}</h3>
          {stationAlerts.length === 0 ? (
            <div className="dashboard__alert-empty-state">
              <CheckCircle className="dashboard__alert-empty-icon" size={32} />
              <p className="dashboard__alert-empty-text">{t('dashboard.cards.alerts.allStationsNormal')}</p>
              <p className="dashboard__alert-empty-subtext">
                {t('dashboard.cards.alerts.noIssuesDetected')}
              </p>
            </div>
          ) : (
            <div className="dashboard__alerts-list">
              {stationAlerts.map((alert, index) => (
                <div key={index} className="dashboard__alert-item">
                  {alert.message}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="dashboard__alerts-section">
          <h3 className="dashboard__alerts-section-title">{t('dashboard.cards.alerts.divergenceTitle')}</h3>
          {divergenceAlerts.length === 0 ? (
            <div className="dashboard__alert-empty-state">
              <TrendingUp className="dashboard__alert-empty-icon" size={32} />
              <p className="dashboard__alert-empty-text">{t('dashboard.cards.alerts.allMetricsOk')}</p>
              <p className="dashboard__alert-empty-subtext">
                {t('dashboard.cards.alerts.divergenceHint')}
              </p>
            </div>
          ) : (
            <div className="dashboard__alerts-list">
              {divergenceAlerts.map((alert, index) => (
                <div key={index} className="dashboard__alert-item">
                  {alert.message}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// Reports Status Card Component
function ReportsStatusCard() {
  const { t } = useTranslation();
  const { navigateTo } = useLanguageNavigation();
  
  // Use same mock data structure as ReportsPage
  const mockReportProgress = {
    // Fascicolo aziendale demo state: 1 required doc uploaded (1/1)
    totalDocuments: 1,
    uploadedDocuments: 1,
    missingDocuments: 0,
    percentComplete: 100,
    canGenerate: true,
  };
  
  // Check if report is being generated (from ReportsPage state)
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  
  const steps = [
    t('dashboard.cards.reportsProgress.steps.collectingStationData'),
    t('dashboard.cards.reportsProgress.steps.processingMeasurements'),
    t('dashboard.cards.reportsProgress.steps.analyzingTrends'),
    t('dashboard.cards.reportsProgress.steps.compilingDocuments'),
    t('dashboard.cards.reportsProgress.steps.generatingPdf'),
  ];
  
  const percentComplete = mockReportProgress.totalDocuments > 0 
    ? (mockReportProgress.uploadedDocuments / mockReportProgress.totalDocuments) * 100 
    : 0;
  
  return (
    <Card className="progress-card" style={{ display: 'flex', flexDirection: 'column', height: '520px' }}>
      <div className="progress-card-header">
        <div className="progress-card-icon-group">
          <div className="progress-card-icon">
            <FileText size={28} />
          </div>
          <h2 className="progress-card-title">{t('dashboard.cards.reportsProgress.title')}</h2>
        </div>
      </div>

      <div className="progress-bar-container" style={{ flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 'var(--spacing-6) 0' }}>
        <div className="circular-progress">
          <svg className="circular-progress-svg" viewBox="0 0 120 120">
            {/* Background circle */}
            <circle
              className="circular-progress-bg"
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke="var(--color-gray-100)"
              strokeWidth="12"
            />
            {/* Progress circle */}
            <circle
              className="circular-progress-fill"
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke={percentComplete === 100 ? "var(--color-success)" : percentComplete > 0 ? "var(--color-primary)" : "var(--color-gray-500)"}
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 54}`}
              strokeDashoffset={`${2 * Math.PI * 54 * (1 - percentComplete / 100)}`}
              transform="rotate(-90 60 60)"
              style={{ transition: 'stroke-dashoffset 500ms ease' }}
            />
          </svg>
          <div className="circular-progress-content">
            <div className="circular-progress-value">{Math.round(percentComplete)}%</div>
            <div className="circular-progress-label">
              {t('dashboard.cards.reportsProgress.progressLabel', {
                uploaded: mockReportProgress.uploadedDocuments,
                total: mockReportProgress.totalDocuments,
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="progress-stats" style={{ flexShrink: 0 }}>
        <div className="progress-stat">
          <div className="progress-stat-value">{mockReportProgress.totalDocuments}</div>
          <div className="progress-stat-label">{t('dashboard.cards.reportsProgress.stats.totalRequired')}</div>
        </div>
        <div className="progress-stat">
          <div className="progress-stat-value progress-stat-value--success">
            {mockReportProgress.uploadedDocuments}
          </div>
          <div className="progress-stat-label">{t('dashboard.cards.reportsProgress.stats.uploaded')}</div>
        </div>
        <div className="progress-stat">
          <div className="progress-stat-value progress-stat-value--warning">
            {mockReportProgress.missingDocuments}
          </div>
          <div className="progress-stat-label">{t('dashboard.cards.reportsProgress.stats.missing')}</div>
        </div>
      </div>
      
      {/* Report Generation Status */}
      {isGenerating && (
        <div style={{ marginTop: 'auto', paddingTop: 'var(--spacing-5)', borderTop: '1px solid var(--color-gray-200)', flexShrink: 0 }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: 'var(--spacing-3)' }}>
            {t('dashboard.cards.reportsProgress.generatingTitle')}
          </h3>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-3)' }}>
            <Loader size={20} style={{ animation: 'spin 1s linear infinite', color: '#4A7A8A', flexShrink: 0 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
              {steps.map((step, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', fontSize: '13px' }}>
                  {index < generationStep ? (
                    <CheckCircle size={14} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
                  ) : index === generationStep ? (
                    <Clock size={14} style={{ color: '#4A7A8A', flexShrink: 0, animation: 'pulse 2s infinite' }} />
                  ) : (
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--color-gray-300)', flexShrink: 0 }} />
                  )}
                  <span style={{ color: index <= generationStep ? 'var(--color-gray-900)' : 'var(--color-gray-600)', fontWeight: index <= generationStep ? 500 : 400 }}>
                    {step}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      <div style={{ marginTop: 'auto', paddingTop: 'var(--spacing-4)', borderTop: '1px solid var(--color-gray-200)', flexShrink: 0 }}>
        <Button
          variant="secondary"
          size="sm"
          icon={<ArrowRight size={16} />}
          onClick={() => navigateTo('/reports')}
          style={{ width: '100%' }}
        >
          {t('dashboard.cards.reportsProgress.viewReports')}
        </Button>
      </div>
    </Card>
  );
}

// Access Card Component
function AccessCard() {
  const { t } = useTranslation();
  const { navigateTo } = useLanguageNavigation();
  const [viewingUser, setViewingUser] = useState(null);
  
  // Get user stats
  const stats = MOCK_STATS;
  const users = MOCK_USERS;
  
  // Get recent logins (limit to 3 to match roles height)
  const recentLogins = users
    .filter(u => u.isActive && u.lastLogin)
    .sort((a, b) => new Date(b.lastLogin) - new Date(a.lastLogin))
    .slice(0, 3);
  
  // Get role breakdown
  const roleBreakdown = [
    { role: 'admin', count: stats.adminUsers, icon: Shield, color: '#3A4A2A' },
    { role: 'reviewer', count: stats.reviewerUsers, icon: FileCheck, color: '#6A5A3A' },
    { role: 'viewer', count: stats.viewerUsers, icon: Eye, color: '#4A4A4A' }
  ];
  
  // Handle role click - navigate to users page with role filter
  const handleRoleClick = (role) => {
    navigateTo('/users', { state: { roleFilter: role } });
  };
  
  // Handle user click - open view user modal
  const handleUserClick = (user) => {
    setViewingUser(user);
  };
  
  return (
    <Card className="dashboard__access-card" hoverable>
      <div className="dashboard__access-header">
        <div className="dashboard__status-icon" style={{ backgroundColor: 'var(--color-primary-light)' }}>
          <Users size={24} style={{ color: 'var(--color-primary)' }} />
        </div>
        <div className="dashboard__status-label">{t('dashboard.cards.access.title')}</div>
        <Button
          variant="secondary"
          size="sm"
          icon={<ArrowRight size={16} />}
          onClick={() => navigateTo('/users')}
          className="dashboard__view-users-btn"
        >
          {t('dashboard.cards.access.viewUsers')}
        </Button>
      </div>
      
      <div className="dashboard__access-content">
        {/* Stats Row */}
        <div className="dashboard__access-stats">
          <div className="dashboard__access-stat">
            <div className="dashboard__access-stat-value">{stats.totalUsers}</div>
            <div className="dashboard__access-stat-label">{t('users.stats.totalUsers')}</div>
          </div>
          <div className="dashboard__access-stat">
            <div className="dashboard__access-stat-value dashboard__access-stat-value--success">
              {stats.activeUsers}
            </div>
            <div className="dashboard__access-stat-label">{t('users.table.status.active')}</div>
          </div>
          <div className="dashboard__access-stat">
            <div className="dashboard__access-stat-value dashboard__access-stat-value--inactive">
              {stats.totalUsers - stats.activeUsers}
            </div>
            <div className="dashboard__access-stat-label">{t('users.table.status.inactive')}</div>
          </div>
        </div>
        
        {/* Two Column Layout: Roles and Last Access */}
        <div className="dashboard__access-bottom">
          {/* Left Column: Role Breakdown */}
          <div className="dashboard__access-roles-wrapper">
            <div className="dashboard__access-roles-label">{t('dashboard.cards.access.rolesTitle')}</div>
            <div className="dashboard__access-roles">
              {roleBreakdown.map((role) => {
                const RoleIcon = role.icon;
                return (
                  <div 
                    key={role.role} 
                    className="dashboard__access-role-item dashboard__access-role-item--clickable"
                    onClick={() => handleRoleClick(role.role)}
                  >
                    <div className="dashboard__access-role-icon" style={{ backgroundColor: `${role.color}14`, color: role.color }}>
                      <RoleIcon size={18} />
                    </div>
                    <div className="dashboard__access-role-info">
                      <div className="dashboard__access-role-label">{t(`users.table.roles.${role.role}`)}</div>
                      <div className="dashboard__access-role-count">{role.count}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Right Column: Last Access */}
          <div className="dashboard__access-last">
            <div className="dashboard__access-last-label">{t('dashboard.cards.access.recentAccessTitle')}</div>
            <div className="dashboard__access-last-list">
              {recentLogins.length > 0 ? (
                recentLogins.map((user) => (
                  <div 
                    key={user.id} 
                    className="dashboard__access-last-user dashboard__access-last-user--clickable"
                    onClick={() => handleUserClick(user)}
                  >
                    <div className="dashboard__access-last-avatar" style={{ backgroundColor: 'var(--color-primary)' }}>
                      {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                    </div>
                    <div className="dashboard__access-last-info">
                      <div className="dashboard__access-last-name">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="dashboard__access-last-time">
                        <Clock size={12} />
                        {formatTimeAgo(user.lastLogin)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="dashboard__access-last-empty">{t('dashboard.cards.access.noRecentAccess')}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* View User Modal */}
      <ViewUserModal
        isOpen={!!viewingUser}
        onClose={() => setViewingUser(null)}
        user={viewingUser}
      />
    </Card>
  );
}

// Type Breakdown Card Component
function TypeBreakdownCard({ type, icon, title, subtitle, count, activeCount, metrics, metricById }) {
  const { t } = useTranslation();
  const variantClass = type === 'campo_aperto'
    ? 'dashboard__type-card--campo'
    : 'dashboard__type-card--retro';
  const comparisonType = type === 'campo_aperto' ? 'retro_modulo' : 'campo_aperto';
  const comparisonTypeLabel = type === 'campo_aperto'
    ? t('dashboard.stationTypes.underPanel.title')
    : t('dashboard.stationTypes.openField.title');
  const totalStations = Number.isFinite(count) ? count : 0;
  const totalActive = Number.isFinite(activeCount) ? activeCount : 0;
  const activePercentage = totalStations > 0
    ? Math.round((totalActive / totalStations) * 100)
    : 0;

  const formatNumber = (value, maximumFractionDigits = 1) => new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits
  }).format(value);

  const buildDeltaText = (currentValue, otherValue, unit, maximumFractionDigits) => {
    const safeCurrent = Number.isFinite(currentValue) ? currentValue : 0;
    const safeOther = Number.isFinite(otherValue) ? otherValue : 0;
    const delta = safeCurrent - safeOther;
    const signedDelta = `${delta > 0 ? '+' : delta < 0 ? '-' : ''}${formatNumber(Math.abs(delta), maximumFractionDigits)}${unit}`;

    if (Math.abs(safeOther) < 0.001) {
      return signedDelta;
    }

    const deltaPercent = (delta / safeOther) * 100;
    const signedPercent = `${deltaPercent > 0 ? '+' : deltaPercent < 0 ? '-' : ''}${formatNumber(Math.abs(deltaPercent), 1)}%`;
    return `${signedDelta} (${signedPercent})`;
  };

  const metricItems = [
    {
      id: 'temp',
      metricId: 'temperature',
      label: t('dashboard.cards.stationType.avgTemp'),
      value: metrics.avgTemp,
      unit: '°C',
      maximumFractionDigits: 1,
      Icon: Thermometer,
    },
    {
      id: 'humidity',
      metricId: 'humidity',
      label: t('dashboard.cards.stationType.avgHumidity'),
      value: metrics.avgHumidity,
      unit: '%',
      maximumFractionDigits: 1,
      Icon: Droplet,
    },
    {
      id: 'wind',
      metricId: 'wind',
      label: t('dashboard.cards.stationType.avgWind'),
      value: metrics.avgWind,
      unit: 'm/s',
      maximumFractionDigits: 1,
      Icon: Wind,
    },
    {
      id: 'solar',
      metricId: 'solar',
      label: t('dashboard.cards.stationType.avgSolar'),
      value: metrics.avgSolar,
      unit: 'W/m²',
      maximumFractionDigits: 0,
      Icon: Sun,
    },
  ];
  
  return (
    <Card className={`dashboard__type-card ${variantClass}`} padding="lg">
      <div className="dashboard__type-card-header">
        <div className="dashboard__type-card-header-main">
          <div className="dashboard__type-card-icon">
            {icon}
          </div>
          <div className="dashboard__type-card-heading">
            <div className="dashboard__type-card-title">{title}</div>
            <div className="dashboard__type-card-subtitle">{subtitle}</div>
          </div>
        </div>
        <div className="dashboard__type-card-kpi">
          <div className="dashboard__type-card-number">{totalStations}</div>
          <div className="dashboard__type-card-unit">{t('dashboard.units.stations')}</div>
          <div className="dashboard__type-card-rate">
            {activePercentage}% {t('stations.status.active')}
          </div>
        </div>
      </div>
      
      <div className="dashboard__type-card-metrics">
        {metricItems.map(({ id, metricId, label, value, unit, maximumFractionDigits, Icon }) => {
          const currentValue = Number.isFinite(value) ? value : 0;
          const compareValue = Number.isFinite(metricById?.[metricId]?.byType?.[comparisonType])
            ? metricById[metricId].byType[comparisonType]
            : 0;
          const deltaText = buildDeltaText(currentValue, compareValue, unit, maximumFractionDigits);

          return (
            <div key={id} className={`dashboard__type-metric dashboard__type-metric--${id}`}>
              <div className="dashboard__type-metric-head">
                <span className="dashboard__type-metric-icon">
                  <Icon size={16} />
                </span>
                <span className="dashboard__type-metric-label" title={label}>{label}</span>
              </div>
              <div className="dashboard__type-metric-reading">
                <span className="dashboard__type-metric-value">{formatNumber(currentValue, maximumFractionDigits)}</span>
                <span className="dashboard__type-metric-unit">{unit}</span>
              </div>
              <div className="dashboard__type-metric-comparison">
                <span className="dashboard__type-metric-comparison-dot" aria-hidden="true" />
                <span className="dashboard__type-metric-comparison-text">
                  {t('dashboard.cards.stationType.vsOtherType', { delta: deltaText, type: comparisonTypeLabel })}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="dashboard__type-card-footer">
        <div className="dashboard__type-card-status-text">
          {t('dashboard.cards.stationType.activeOfTotal', { active: totalActive, total: totalStations })}
        </div>
      </div>
    </Card>
  );
}

// Metric Card Component (continued in next file due to size)
// Station Mini Card Component (continued in next file due to size)

// Metric Card with Chart and Type Comparison
function MetricCard({ metric }) {
  const { t } = useTranslation();
  const Icon = metric.id === 'temperature' ? Thermometer :
               metric.id === 'humidity' ? Droplet :
               metric.id === 'wind' ? Wind : Sun;

  const metricLabel = (() => {
    switch (metric.id) {
      case 'temperature':
        return t('analytics.metrics.temperature');
      case 'humidity':
        return t('analytics.metrics.humidity');
      case 'wind':
        return t('analytics.metrics.windSpeed');
      case 'solar':
        return t('analytics.metrics.radiation');
      default:
        return metric.label || metric.id;
    }
  })();
  
  const difference = (type) => {
    const diff = metric.byType[type] - metric.byType.all;
    return diff.toFixed(1);
  };
  
  return (
    <Card className="dashboard__metric-card">
      {/* Header */}
      <div className="dashboard__metric-header">
        <div>
          <div className="dashboard__metric-icon" style={{ backgroundColor: `${metric.color}14` }}>
            <Icon size={28} style={{ color: metric.color }} />
          </div>
          <div className="dashboard__metric-label">{metricLabel}</div>
        </div>
        {metric.trend && (
          <div className={`dashboard__metric-trend dashboard__metric-trend--${metric.trend.direction}`}>
            {metric.trend.direction === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            <span>{Math.abs(metric.trend.value)}%</span>
          </div>
        )}
      </div>
      
      {/* Primary Value */}
      <div className="dashboard__metric-value">
        <span className="dashboard__metric-number" style={{ color: metric.color }}>
          {metric.value}
        </span>
        <span className="dashboard__metric-unit">{metric.unit}</span>
      </div>
      
      {/* Metadata */}
      <div className="dashboard__metric-meta">
        <span>
          <Clock size={14} />
          {formatTimeAgo(metric.lastUpdate)}
        </span>
        <span>
          <CheckCircle size={14} style={{ color: 'var(--color-success)' }} />
          {t('dashboard.cards.metric.status.good')}
        </span>
      </div>
      
      {/* Chart */}
      <div className="dashboard__metric-chart">
        <ResponsiveContainer width="100%" height={100}>
          {metric.id === 'humidity' ? (
            <AreaChart data={metric.chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" horizontal vertical={false} />
              <Tooltip />
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke={metric.color} 
                fill={metric.color}
                fillOpacity={0.14}
                strokeWidth={2.5}
              />
            </AreaChart>
          ) : metric.id === 'solar' ? (
            <BarChart data={metric.chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" horizontal vertical={false} />
              <Tooltip />
              <Bar dataKey="value" fill={metric.color} radius={[8, 8, 0, 0]} />
            </BarChart>
          ) : (
            <LineChart data={metric.chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" horizontal vertical={false} />
              <Tooltip />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={metric.color} 
                strokeWidth={2.5}
                dot={false}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
      
      {/* Type Comparison */}
      <div className="dashboard__metric-comparison">
        <div className="dashboard__comparison-title">{t('dashboard.cards.metric.byStationTypeTitle')}</div>
        <div className="dashboard__comparison-grid">
          <div className="dashboard__comparison-item">
            <div className="dashboard__comparison-label">
              <span className="dashboard__comparison-dot" style={{ backgroundColor: 'var(--color-gray-500)' }} />
              {t('dashboard.cards.metric.allStations')}
            </div>
            <div className="dashboard__comparison-value">
              {metric.byType.all}{metric.unit}
            </div>
          </div>
          <div className="dashboard__comparison-item">
            <div className="dashboard__comparison-label">
              <span className="dashboard__comparison-dot" style={{ backgroundColor: 'var(--color-success)' }} />
              {t('dashboard.stationTypes.openField.title')}
            </div>
            <div className="dashboard__comparison-value">
              {metric.byType.campo_aperto}{metric.unit}
            </div>
            <div className={`dashboard__comparison-diff ${parseFloat(difference('campo_aperto')) > 0 ? 'dashboard__comparison-diff--positive' : 'dashboard__comparison-diff--negative'}`}>
              {parseFloat(difference('campo_aperto')) > 0 ? '+' : ''}{difference('campo_aperto')}{metric.unit}
            </div>
          </div>
          <div className="dashboard__comparison-item">
            <div className="dashboard__comparison-label">
              <span className="dashboard__comparison-dot" style={{ backgroundColor: 'var(--color-primary)' }} />
              {t('dashboard.stationTypes.underPanel.title')}
            </div>
            <div className="dashboard__comparison-value">
              {metric.byType.retro_modulo}{metric.unit}
            </div>
            <div className={`dashboard__comparison-diff ${parseFloat(difference('retro_modulo')) > 0 ? 'dashboard__comparison-diff--positive' : 'dashboard__comparison-diff--negative'}`}>
              {parseFloat(difference('retro_modulo')) > 0 ? '+' : ''}{difference('retro_modulo')}{metric.unit}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
